import {CompreAgoraAnalyticRow} from './compreAgoraBundleParser'
import {ProductThumbnail} from '../shared/components/ProductThumbnail'

interface CompreAgoraPreviewTableProps {
  rows: CompreAgoraAnalyticRow[]
}

type Column = {
  key: keyof CompreAgoraAnalyticRow
  label: string
  kind?: 'text' | 'number' | 'currency' | 'month' | 'classification'
}

const COLUMNS: Column[] = [
  {key: 'anoMes', label: 'AnoMes', kind: 'month'},
  {key: 'cnpjLoja', label: 'CNPJ Loja'},
  {key: 'gtin', label: 'GTIN'},
  {key: 'produto', label: 'Produto'},
  {key: 'fornecedor', label: 'Fornecedor'},
  {key: 'vendaQtdMes', label: 'Venda (Qtd) Mês', kind: 'number'},
  {key: 'vendaRs', label: 'Venda (R$)', kind: 'currency'},
  {key: 'comprasQtd', label: 'Compras (Qtd)', kind: 'number'},
  {key: 'comprasRs', label: 'Compras (R$)', kind: 'currency'},
  {key: 'estoqueAtualQtd', label: 'Estoque Atual (Qtd)', kind: 'number'},
  {key: 'precoMedioRs', label: 'Preço Médio (R$)', kind: 'currency'},
  {key: 'diasDoMes', label: 'Dias do Mês', kind: 'number'},
  {key: 'cmd', label: 'CMD', kind: 'number'},
  {key: 'coberturaAtualDias', label: 'Cobertura Atual (dias)', kind: 'number'},
  {key: 'ltDias', label: 'LT (dias)', kind: 'number'},
  {key: 'flagLtDefault', label: 'Flag LT Default', kind: 'number'},
  {key: 'coberturaAlvoDias', label: 'Cobertura Alvo (dias)', kind: 'number'},
  {key: 'flagCoberturaDefault', label: 'Flag Cobertura Default', kind: 'number'},
  {key: 'ss', label: 'SS', kind: 'number'},
  {key: 'ddlt', label: 'DDLT', kind: 'number'},
  {key: 'rop', label: 'ROP', kind: 'number'},
  {key: 'estoqueAlvo', label: 'Estoque Alvo', kind: 'number'},
  {key: 'pedidoSugerido', label: 'Pedido Sugerido', kind: 'number'},
  {key: 'flagSemVenda', label: 'Flag Sem Venda', kind: 'number'},
  {key: 'qtdAjustadaUsuario', label: 'Qtd Ajustada Usuário', kind: 'number'},
  {key: 'pedidoFinalQtd', label: 'Pedido Final (Qtd)', kind: 'number'},
  {key: 'origemPedido', label: 'Origem do Pedido'},
  {key: 'flagAjustado', label: 'Flag Ajustado', kind: 'number'},
  {key: 'classificacao', label: 'Classificação', kind: 'classification'},
  {key: 'chave', label: 'Chave'},
]

const numberFormatter = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 4,
})

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

function renderValue(row: CompreAgoraAnalyticRow, column: Column) {
  const value = row[column.key]
  if (value === null || value === '') return '—'
  if (column.kind === 'month') {
    const [year, month] = String(value).split('-')
    return `${month}/${year}`
  }
  if (column.kind === 'currency') return currencyFormatter.format(Number(value))
  if (column.kind === 'number') return numberFormatter.format(Number(value))
  if (column.kind === 'classification') {
    return (
      <span className='inline-flex whitespace-nowrap rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700'>
        {String(value)}
      </span>
    )
  }
  return String(value)
}

export function CompreAgoraPreviewTable({rows}: CompreAgoraPreviewTableProps) {
  return (
    <div className='overflow-hidden rounded-lg border border-gray-200'>
      <div className='max-h-[600px] overflow-auto'>
        <table className='w-full min-w-max'>
          <thead className='sticky top-0 z-10 border-b border-gray-200 bg-gray-50'>
            <tr>
              <th className='whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase text-gray-700'>Imagem</th>
              {COLUMNS.map((column) => (
                <th
                  key={column.key}
                  className={`whitespace-nowrap px-3 py-3 text-xs font-semibold uppercase text-gray-700 ${
                    column.kind === 'number' || column.kind === 'currency'
                      ? 'text-right'
                      : 'text-left'
                  }`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200 bg-white'>
            {rows.slice(0, 50).map((row) => (
              <tr key={`${row.chave}-${row.produto}`} className='hover:bg-gray-50'>
                <td className='px-3 py-2'>
                  <ProductThumbnail src={row.foto} alt={row.produto} ean={row.ean} size='sm' />
                </td>
                {COLUMNS.map((column) => (
                  <td
                    key={column.key}
                    className={`max-w-xs whitespace-nowrap px-3 py-3 text-sm text-gray-900 ${
                      column.kind === 'number' || column.kind === 'currency'
                        ? 'text-right tabular-nums'
                        : ''
                    } ${column.key === 'produto' ? 'max-w-md truncate' : ''} ${
                      column.key === 'gtin' || column.key === 'cnpjLoja' || column.key === 'chave'
                        ? 'font-mono'
                        : ''
                    }`}
                    title={String(row[column.key] ?? '')}
                  >
                    {renderValue(row, column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 50 && (
        <div className='border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600'>
          Mostrando 50 de {rows.length} registros analíticos
        </div>
      )}
    </div>
  )
}
